<template>
  <div v-if="visible" class="server-modal-overlay" @click.self="handleCancel">
    <div class="server-modal-content">
      <h2 class="title noselect">服务器配置</h2>
      <div class="server-setting-container">
        <!-- 当前服务器地址 -->
        <div class="form-group">
          <label class="label noselect">当前服务器</label>
          <input class="input" type="text" disabled :value="currentServer" />
        </div>
        
        <!-- 输入新服务器地址 -->
        <div class="form-group">
          <label class="label noselect">输入新服务器地址</label>
          <input 
            class="input" 
            type="text" 
            placeholder="例如：api.example.com 或 118.31.13.222:8443" 
            v-model="newServerInput"
            ref="inputRef"
          />
        </div>
        
        <!-- 按钮组 -->
        <div class="btn-group">
          <button class="btn btn-cancel noselect" @click="handleCancel">取消</button>
          <button class="btn btn-confirm noselect" @click="handleConfirm" :disabled="isConfirming">
            {{ isConfirming ? '验证中...' : '确认' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, nextTick, onMounted } from 'vue'
import { getKeyStr ,removeKey, setKey, setKeyJsonStr ,getKeyJson,updateKeyJson,getsubKey, removeKeys} from 'd3components/src/utils/localStorage';

import { toast } from 'd3components/src/utils/tools'
import { Capacitor } from '@capacitor/core'

interface Props {
  visible?: boolean
}

interface Emits {
  (e: 'update:visible', value: boolean): void
  (e: 'confirm'): void
}

const props = withDefaults(defineProps<Props>(), {
  visible: false
})

const emit = defineEmits<Emits>()

// ===== 状态管理 =====
const currentServer = ref('') // 当前服务器地址
const newServerInput = ref('') // 用户输入的新服务器地址
const inputRef = ref<HTMLInputElement>()
const isConfirming = ref(false) // 确认按钮加载状态

// ===== 初始化：加载当前服务器配置 =====
onMounted(() => {
  loadCurrentServer()
})

// 从本地存储和环境变量加载当前服务器
const loadCurrentServer = () => {
  const savedServer = getsubKey('mobile', 'server_info')
  currentServer.value = (savedServer && savedServer.host) || import.meta.env.VITE_BASE_API || '未设置'
}

// ===== 弹窗打开时聚焦输入框 =====
watch(() => props.visible, (newVal) => {
  if (newVal) {
    nextTick(() => {
      inputRef.value?.focus()
    })
  }
})

// ===== 验证服务器地址 =====
const validateServer = async (serverUrl: string): Promise<any> => {
  const trimmed = serverUrl.trim()
  if (!trimmed) {
    throw new Error('请输入服务器地址')
  }
  
  // 构建完整 URL（带协议）
  let fullUrl = ''
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    // 用户已经输入了完整的协议，直接使用
    fullUrl = trimmed
  } else {
    // 用户没有输入协议，需要添加
    // 判断是否是 IP 地址，IP 用 http，域名用 https
    const isIP = /^(\d{1,3}\.){3}\d{1,3}/.test(trimmed)
    fullUrl = isIP ? `http://${trimmed}` : `https://${trimmed}`
  }
  
  // 构建提交数据：移除协议前缀
  // 有端口：118.31.13.222:8443
  // 无端口：www.d3inf.com
  const submitData = trimmed.replace(/^https?:\/\//, '')
  
  // 尝试三种路径：/iam/get_server_info -> /api/get_server_info -> 用户原始地址
  const attempts = [
    { path: '/iam/', suffix: 'get_server_info' },
    { path: '/api/', suffix: 'get_server_info' },
    { path: '', suffix: '' } // 第三次：直接使用用户输入的地址
  ]
  
  let lastError: any = null
  
  for (let i = 0; i < attempts.length; i++) {
    const { path, suffix } = attempts[i]
    try {
      let apiUrl = ''
      if (i === 2) {
        // 第三次：直接使用用户输入的原始地址
        apiUrl = fullUrl
      } else {
        // 第一、二次：拼接路径
        // 确保 fullUrl 以 / 结尾或 path 以 / 开头，避免重复斜杠
        const separator = fullUrl.endsWith('/') || path.startsWith('/') ? '' : '/'
        apiUrl = `${fullUrl}${separator}${path}${suffix}`
      }
      
      const response: any = await Promise.race([
        fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ host: submitData })
        }).then(async (res) => {
          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`)
          }
          return await res.json()
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('请求超时')), 5000)
        )
      ])
      
      // 如果请求成功，直接返回（包含成功的路径信息）
      if (response && response.code === 0) {
        // 返回响应数据，并附加成功的请求信息
        return {
          ...response,
          _requestInfo: {
            successPath: path,           // 成功的 IAM 路径，如 '/iam/' 或 '/api/'
            successUrl: apiUrl,          // 完整的请求 URL
            host: fullUrl,               // 服务器地址（带协议）
            submitHost: submitData,      // 提交的 host（不带协议）
            attemptNumber: i + 1         // 第几次尝试成功
          }
        }
      }
      
      lastError = response
    } catch (error) {
      lastError = error
      // 静默失败，继续尝试下一个路径
    }
  }
  
  // 所有路径都失败，抛出错误
  throw new Error('服务器验证失败，请检查地址是否正确')
}

// ===== 更新URL中的租户参数 =====
const updateUrlTenantParam = (tenantId: string) => {
  try {
    // App环境不需要更新URL
    if (Capacitor.isNativePlatform()) {
      console.log('[ServerConfig] App环境，跳过URL更新')
      return
    }
    
    // Web/小程序环境：更新URL参数
    const hash = window.location.hash
    const [path, query] = hash.split('?')
    const params = new URLSearchParams(query || '')
    
    // 设置或更新 orgid 参数
    params.set('orgid', tenantId)
    
    // 构建新的URL
    const newHash = `${path}?${params.toString()}`
    
    // 使用 replaceState 更新URL，不触发页面跳转
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${newHash}`)
    
    console.log(`[ServerConfig] URL已更新: orgid=${tenantId}`)
  } catch (error) {
    console.error('[ServerConfig] 更新URL失败:', error)
  }
}

// ===== 确认按钮：验证并保存服务器配置 =====
const confirmServer = async (serverUrl?: string) => {
  const inputServer = (serverUrl ?? newServerInput.value).trim()
  newServerInput.value = inputServer

  if (!inputServer) {
    toast('请输入服务器地址', 2)
    return
  }
  
  if (isConfirming.value) return
  
  isConfirming.value = true
  
  try {
    // 验证服务器地址（三次尝试）
    const response: any = await validateServer(inputServer)
    
    // 检查响应是否成功
    if (response.code === 0 && response.value) {
      const serverInfo = response.value
      
      // 保存服务器信息到本地存储
      updateKeyJson('mobile', 'server_info', {
        host: response._requestInfo.host,
        iam: response._requestInfo.successPath
      })
      
      // 设置租户 ID
      if (serverInfo.orgid) {
        updateKeyJson('mobile', 'tenant_id', serverInfo.orgid)
        // Web/小程序环境：同步更新URL参数
        updateUrlTenantParam(serverInfo.orgid)
      }
      
      toast('服务器配置成功，即将刷新页面', 1)
      
      // 延迟后关闭弹窗并刷新页面
      setTimeout(() => {
        emit('update:visible', false)
        emit('confirm')
        location.reload()
      }, 1000)
      
    } else {
      toast(response.msg || '服务器验证失败，请检查地址', 3)
    }
    
  } catch (error: any) {
    // 三次请求都失败后，只提示一次
    const errorMsg = error.message || '服务器验证失败，请检查地址'
    toast(errorMsg, 3)
  } finally {
    isConfirming.value = false
  }
}

const handleConfirm = async () => {
  await confirmServer()
}

// ===== 取消按钮 =====
const handleCancel = () => {
  newServerInput.value = ''
  emit('update:visible', false)
}

defineExpose({
  confirmServer
})
</script>

<style lang="scss" scoped>
.noselect {
  -webkit-user-select: none;
  /* Safari */
  -moz-user-select: none;
  /* Firefox */
  -ms-user-select: none;
  /* Internet Explorer/Edge */
  user-select: none;
  /* Standard syntax */
}
.server-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease;
}

.server-modal-content {
  border-radius: 18px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  // background: white;
  background-color: var(--D3-bg-color-container);
  animation: slideUp 0.3s ease;
}

.server-setting-container {
  padding: 40px 50px;
  border-radius: 18px;
  margin: 20px;
}

.title {
  text-align: center;
  font-size: 40px;
  font-weight: 600;
  padding-top: 40px;
  color: var(--color-text);
  margin: 0;
}

.form-group {
  margin-bottom: 28px;
}

.label {
  display: block;
  font-size: 28px;
  color: var(--color-text);
  margin-bottom: 8px;
}

.input {
  width: 100%;
  height: 80px;
  padding: 0 12px;
  // border: 1px solid #dcdfe6;
  border: 1px solid var(--D3-border-color);
  border-radius: 8px;
  outline: none;
  transition: all 0.2s;
  font-size: 28px;
  box-sizing: border-box;
  color: var(--D3-font-color);
  background-color: var(--card-background);
}

// .input:focus {
//   border-color: #409eff;
//   box-shadow: 0 0 5px rgba(64, 158, 255, 0.4);
// }

.input[disabled] {
  // background: #f5f7fa;
  cursor: not-allowed;
  color: #999;
}

.input::placeholder {
  font-size: 24px;
  color: #999;
}

.btn-group {
  display: flex;
  justify-content: space-between;
  margin-top: 40px;
  gap: 20px;
}

.btn {
  flex: 1;
  line-height: 80px;
  border-radius: 8px;
  font-size: 28px;
  cursor: pointer;
  border: none;
  transition: 0.2s;
}

.btn-cancel {
  background: #f2f3f5;
  color: #666;
}

.btn-cancel:hover {
  background: #e5e6e7;
}

.btn-confirm {
  background: linear-gradient(135deg, rgba(57, 140, 254, 1) 0%, rgba(26, 111, 220, 1) 100%);
  color: white;
  box-shadow: 0 4px 16px rgba(57, 140, 254, 0.3);
  position: relative;
  overflow: hidden;
}

.btn-confirm::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

.btn-confirm:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(57, 140, 254, 0.45);
}

.btn-confirm:hover:not(:disabled)::before {
  left: 100%;
}

.btn-confirm:active:not(:disabled) {
  transform: translateY(0) scale(0.98);
  box-shadow: 0 2px 10px rgba(57, 140, 254, 0.3);
}

.btn-confirm:disabled {
  background: linear-gradient(135deg, rgba(57, 140, 254, 0.5) 0%, rgba(26, 111, 220, 0.5) 100%);
  cursor: not-allowed;
  box-shadow: none;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* 移动端适配 */
@media (max-width: 768px) {
  .server-modal-content {
    width: 95%;
  }

  .server-setting-container {
    padding: 30px 20px;
    margin: 10px;
  }

  .title {
    font-size: 32px;
    padding-top: 20px;
  }

  .label {
    font-size: 24px;
  }

  .input {
    height: 70px;
    font-size: 24px;
  }

  .btn {
    line-height: 70px;
    font-size: 24px;
  }
}
</style>
